import type { BaseTicket, TravellerClassVariant } from '@/types/buyTicket';
import { TRAVELLER_CLASS_ADULT, TRAVELLER_CLASS_PREFIX } from './ticketClass';

const PREFERRED_TRAVELLER_CLASS = TRAVELLER_CLASS_ADULT;

// A product may combine multiple traveller classes into one group ticket only
// when it carries this tag; without it the product is not groupable.
const GROUPABLE_TAG = 'groupable';

export interface GroupableTicket {
  product_set_id: string;
  title: string;
  currency: string;
  amount: number;
  tags: string[];
}

const getProductGroupKey = (tags: string[]): string =>
  tags
    .filter((tag) => !tag.startsWith(TRAVELLER_CLASS_PREFIX))
    .slice()
    .sort()
    .join('|');

/**
 * Collapses traveller-class variants of the same product into a single base
 * ticket. Variants are grouped by their non-traveller-class tags; the adult
 * variant (tc_a) is preferred as the representative, falling back to the first.
 * Each variant is kept (keyed by traveller class) so a single-traveller
 * purchase can reuse the existing product set instead of creating a group
 * ticket.
 */
export const groupTicketsByProduct = (
  items: GroupableTicket[],
): BaseTicket[] => {
  const groups = new Map<string, GroupableTicket[]>();

  items.forEach((item) => {
    const groupKey = getProductGroupKey(item.tags);
    const existing = groups.get(groupKey);
    if (existing) {
      existing.push(item);
    } else {
      groups.set(groupKey, [item]);
    }
  });

  return Array.from(groups.values()).map((groupVariants) => {
    const representative =
      groupVariants.find((variant) =>
        variant.tags.includes(PREFERRED_TRAVELLER_CLASS),
      ) ?? groupVariants[0];

    const variants: Record<string, TravellerClassVariant> = {};
    groupVariants.forEach((variant) => {
      variant.tags
        .filter((tag) => tag.startsWith(TRAVELLER_CLASS_PREFIX))
        .forEach((travellerClass) => {
          if (!variants[travellerClass]) {
            variants[travellerClass] = {
              product_set_id: variant.product_set_id,
              amount: variant.amount,
              tags: variant.tags,
            };
          }
        });
    });

    return {
      product_set_id: representative.product_set_id,
      title: representative.title,
      currency: representative.currency,
      tags: representative.tags,
      availableTravellerClasses: Object.keys(variants).sort(),
      allowsMultipleTravellerClasses:
        representative.tags.includes(GROUPABLE_TAG),
      variants,
    };
  });
};
